import { DirectionalHint, IconButton, Stack, TooltipHost } from '@fluentui/react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeRaw from 'rehype-raw'
import DOMPurify from 'dompurify'
import styles from './CitationPanel.module.css'
import { useChatContext } from '../../state/ChatProvider'
import { Citation, ExecResults } from '../../api'
import { XSSAllowTags } from '../../constants/sanatizeAllowables'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { useContext } from 'react'
import { nord } from 'react-syntax-highlighter/dist/esm/styles/prism'
import { CloseIcon } from '../../assets'
import { AppStateContext } from '../../state/AppProvider'

const CitationPanel = () => {
  const appStateContext = useContext(AppStateContext)
  const {
    messages,
    isIntentsPanelOpen,
    setIsIntentsPanelOpen,
    activeCitation,
    setActiveCitation,
    isCitationPanelOpen,
    setIsCitationPanelOpen
  } = useChatContext()
  const citation: Citation | undefined = activeCitation?.citation
  const answerId = 5

  if (!activeCitation || !citation) return null

  const handleCitationPanelClose = () => {
    setIsCitationPanelOpen(false)
    setActiveCitation(undefined)
  }

  return (
    <>
      {messages && messages.length > 0 && isCitationPanelOpen && citation && (
        <Stack.Item className={styles.citationPanel} tabIndex={0} role="tabpanel" aria-label="Citations Panel">
          <Stack
            aria-label="Citations Panel Header Container"
            horizontal
            className={styles.citationPanelHeaderContainer}
            horizontalAlign="space-between"
            verticalAlign="center">
            <span aria-label="Citations" className={styles.citationPanelHeader}>
              References
            </span>
            <TooltipHost
              content={'Close references panel'}
              id="closeReferences"
              directionalHint={DirectionalHint.leftCenter}>
              <IconButton
                styles={{
                  root: {
                    width: '36px',
                    height: '36px'
                  },
                  rootHovered: { borderRadius: '8px' }
                }}
                aria-describedby='closeReferences'
                aria-label="Close references panel"
                onClick={handleCitationPanelClose}
              >
                <img src={CloseIcon} alt="" />
              </IconButton>
            </TooltipHost>
          </Stack>
          <h5
            className={styles.citationPanelTitle}
            tabIndex={0}
            title={
              citation.url && !citation.url.includes('blob.core')
                ? citation.url
                : (citation.title ?? '')
            }>
            {citation.url ?
              <a href={citation.url} target="_blank" rel="noopener noreferrer">
                {citation.title}
              </a>
              : <span>{citation.title}</span>}
          </h5>
          <div tabIndex={0}>
            <ReactMarkdown
              linkTarget="_blank"
              className={styles.citationPanelContent}
              children={DOMPurify.sanitize(citation.content, { ALLOWED_TAGS: XSSAllowTags })}
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
            />
          </div>
        </Stack.Item>
      )}

      {messages && messages.length > 0 && isIntentsPanelOpen && (
        <Stack.Item className={styles.citationPanel} tabIndex={0} role="tabpanel" aria-label="Intents Panel">
          <Stack
            aria-label="Intents Panel Header Container"
            horizontal
            className={styles.citationPanelHeaderContainer}
            horizontalAlign="space-between"
            verticalAlign="center">
            <span aria-label="Intents" className={styles.citationPanelHeader}>
              Intents
            </span>
            <IconButton
              iconProps={{ iconName: 'Cancel' }}
              aria-label="Close intents panel"
              onClick={() => setIsIntentsPanelOpen(false)}
            />
          </Stack>
          <Stack horizontalAlign="space-between">
            {appStateContext?.state?.answerExecResult[answerId]?.map((execResult: ExecResults, index) => (
              <Stack className={styles.exectResultList} verticalAlign="space-between">
                <>
                  <span>Intent:</span> <p>{execResult.intent}</p>
                </>
                {execResult.search_query && (
                  <>
                    <span>Search Query:</span>
                    <SyntaxHighlighter
                      style={nord}
                      wrapLines={true}
                      lineProps={{ style: { wordBreak: 'break-all', whiteSpace: 'pre-wrap' } }}
                      language="sql"
                      PreTag="p">
                      {execResult.search_query}
                    </SyntaxHighlighter>
                  </>
                )}
                {execResult.search_result && (
                  <>
                    <span>Search Result:</span> <p>{execResult.search_result}</p>
                  </>
                )}
                {execResult.code_generated && (
                  <>
                    <span>Code Generated:</span>
                    <SyntaxHighlighter
                      style={nord}
                      wrapLines={true}
                      lineProps={{ style: { wordBreak: 'break-all', whiteSpace: 'pre-wrap' } }}
                      language="python"
                      PreTag="p">
                      {execResult.code_generated}
                    </SyntaxHighlighter>
                  </>
                )}
              </Stack>
            ))}
          </Stack>
        </Stack.Item>
      )}
    </>
  )
}

export default CitationPanel
